import { getToken, refreshToken, login } from "@/scripts/keycloak.js";

// Set by main.js once the store exists, so this module stays free of store imports.
let showError = null;

export function setApiErrorReporter(fn) {
  showError = fn;
}

/**
 * Turns an API failure into something the user can see. Without this a denied request
 * looked exactly like nothing happening at all.
 */
export function reportApiError(status, url) {
  if (status === 403) {
    notify("You do not have permission to do that.");
  } else if (status === 404) {
    // Assets you may not see are reported as missing; only worth a line in the log.
    window.logRed("not found (or not permitted): " + url);
  } else if (status) {
    notify("The server rejected the request (" + status + ").");
  } else {
    notify("Could not reach the server.");
  }
}

/**
 * The endpoint-specific half of a failure: installFailureHandling already showed the generic
 * one, but a 400 carries a message only the caller knows what to do with.
 */
export function endpointMessage(error) {
  const response = error.response;
  return (response && ((response.body && response.body.message) || response.text)) || error.message || "Request failed";
}

/** Refresh once, then send the user back to Keycloak if that did not help. */
export async function refreshOrLogin() {
  if (await refreshToken()) return true;
  login();
  return false;
}

/**
 * Gives the generated client the same treatment the axios interceptors give: one silent retry
 * after a refreshed token, and a visible error otherwise. It speaks superagent, so those
 * interceptors never see it - and most of the app's requests, including every asset mutation,
 * go through it. callApi is the one funnel they all pass through.
 */
export function installFailureHandling(apiClient) {
  const callApi = apiClient.callApi.bind(apiClient);

  apiClient.callApi = function (...args) {
    const params = args.slice(0, -1);
    const last = args[args.length - 1];
    const callback = typeof last === "function" ? last : () => {};
    let retried = false;

    const handle = async (error, data, response) => {
      if (!error) return callback(error, data, response);

      // The token has usually just expired between requests; retry once with a fresh one.
      if (error.status === 401 && !retried) {
        retried = true;
        if (await refreshOrLogin()) {
          apiClient.defaultHeaders = { ...apiClient.defaultHeaders, Authorization: "Bearer " + getToken() };
          callApi(...params, handle);
        }
        return;
      }

      reportApiError(error.status, params[0]);
      return callback(error, data, response);
    };

    return callApi(...params, handle);
  };

  return apiClient;
}

function notify(message) {
  if (showError) {
    showError(message);
  } else {
    window.logRed(message);
  }
}
